import { useState, useEffect } from "react";
import { BsPrinter } from "react-icons/bs";
import { HiXMark } from "react-icons/hi2";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaBars } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import AddBill from "./AddBill";
import { PrintLayout } from "../PrintLayout";
import { PathologyPrintSlip } from "./PathologyPrintSlip";

const API_URL = "https://api.muktihospital.com/api/pathology-bill";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

// Convert a number to Bangladeshi words (Lakh/Crore system)
const numberToWordsBD = (value) => {
  let num = Math.floor(Number(value) || 0);
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const three = (n) => {
    const h = Math.floor(n / 100);
    const r = n % 100;
    return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
  };
  let words = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) words += three(crore) + " Crore ";
  if (lakh) words += two(lakh) + " Lakh ";
  if (thousand) words += two(thousand) + " Thousand ";
  if (num) words += three(num);
  return words.trim();
};

// Helper function to safely parse tests data
const parseTestsData = (tests) => {
  if (Array.isArray(tests)) {
    return tests;
  }
  if (typeof tests === "string") {
    try {
      const parsed = JSON.parse(tests);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error("Error parsing tests JSON:", error);
      return [];
    }
  }
  return [];
};

const BillDetailsModal = ({
  isOpen,
  onClose,
  billDetails,
  onDelete,
  onSave,
  isReadOnly,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [printType, setPrintType] = useState(null); // null | "bill" | "test"

  // Render the chosen layout first, then trigger print.
  // Must stay ABOVE the early return so hook order never changes.
  useEffect(() => {
    if (!printType) return;
    const timer = setTimeout(() => {
      const elementsToHide = document.querySelectorAll(".print-hidden");
      elementsToHide.forEach((el) => (el.style.display = "none"));
      const selector =
        printType === "bill" ? ".print-layout" : ".print-test-report-layout";
      const layout = document.querySelector(selector);
      if (layout) layout.style.display = "block";
      window.print();
      setTimeout(() => {
        elementsToHide.forEach((el) => (el.style.display = ""));
        if (layout) layout.style.display = "none";
        setPrintType(null);
      }, 600);
    }, 80);
    return () => clearTimeout(timer);
  }, [printType]);

  if (!isOpen || !billDetails) return null;

  const {
    id,
    billNumber,
    referDoctorName,
    patientName,
    doctorName,
    discount,
    discountType,
    paymentMode,
    paymentAmount,
    total,
    taxAmount,
    netAmount,
    dueAmount,
    returnAmount,
    tests: rawTests,
    createdAt,
    donorName,
    authorizedBy,
    payments,
  } = billDetails;

  // Parse tests data safely
  const tests = parseTestsData(rawTests);

  const handlePrintTestReport = () => setPrintType("test");

  const handlePrintBill = () => setPrintType("bill");

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/delete/${id}`, {
        headers: { "x-api-key": API_KEY },
      });
      toast.success("Bill deleted successfully!", {
        autoClose: 3000,
        position: "top-right",
      });
      onDelete(id);
      setTimeout(() => onClose(), 3000);
    } catch (err) {
      console.error("Error deleting bill:", err.response || err.message);
      toast.error("Failed to delete bill.", {
        autoClose: 3000,
        position: "top-right",
      });
    }
  };

  // ===== Test Report (old layout) data =====
  const billInfoContentTestReport = (
    <>
      <div>
        <p>
          <strong>Bill No:</strong> {billNumber || "N/A"}
        </p>
        <p>
          <strong>Date:</strong>{" "}
          {createdAt
            ? `${new Date(createdAt).toLocaleDateString("en-US")} ${new Date(
                createdAt
              ).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })}`
            : "N/A"}
        </p>
      </div>
      <div>
        <p>
          <strong>Patient Name:</strong> {patientName || "N/A"}
        </p>
        <p>
          <strong>Referring Doctor:</strong> {referDoctorName || "N/A"}
        </p>
      </div>
    </>
  );

  const tableContent = (
    <>
      <thead>
        <tr>
          <th className="uppercase">Id</th>
          <th className="uppercase">Test Name</th>
          <th className="uppercase">Vat (%)</th>
          <th className="uppercase">Amount (৳)</th>
        </tr>
      </thead>
      <tbody>
        {tests && tests.length > 0 ? (
          tests.map((test, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{test.testName || "N/A"}</td>
              <td>
                {test.taxPercent
                  ? `${test.taxPercent}% (৳${(
                      (test.amount * test.taxPercent) /
                      100
                    ).toFixed(2)})`
                  : "0% (৳0.00)"}
              </td>
              <td>৳{test.amount ? test.amount.toFixed(2) : "0.00"}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="6" style={{ textAlign: "center" }}>
              No tests available
            </td>
          </tr>
        )}
      </tbody>
    </>
  );

  // ===== NEW A5 B&W slip data (no VAT, with PAID/DUE stamp + amount in words) =====
  const slipBillInfo = (
    <>
      <div>
        <p><strong>Bill No:</strong> {billNumber || "N/A"}</p>
        <p>
          <strong>Date:</strong>{" "}
          {createdAt
            ? `${new Date(createdAt).toLocaleDateString("en-GB")} ${new Date(
                createdAt
              ).toLocaleTimeString("en-US", {
                hour: "numeric",
                minute: "numeric",
                hour12: true,
              })}`
            : "N/A"}
        </p>
        <p><strong>Patient Name:</strong> {patientName || "N/A"}</p>
        <p><strong>Mobile:</strong> {billDetails.patient?.phoneNumber || "N/A"}</p>
      </div>
      <div>
        <p><strong>Donor:</strong> {donorName || "N/A"}</p>
        <p><strong>Referring Doctor:</strong> {referDoctorName || "N/A"}</p>
        <p><strong>Doctor Name:</strong> {doctorName || "N/A"}</p>
        <p><strong>Authorized By:</strong> {authorizedBy || "N/A"}</p>
      </div>
    </>
  );

  const slipTable = (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="bg-gray-100">
          <th className="p-2 sm:p-3 border font-semibold">Test Name</th>
          <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
        </tr>
      </thead>
      <tbody>
        {tests.map((test, index) => (
          <tr key={index} className="border-t">
            <td className="p-2 sm:p-3 border">{test.testName || "N/A"}</td>
            <td className="p-2 sm:p-3 border text-right">
              {(Number(test.amount) || 0).toFixed(2)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const slipSummary = (
    <div className="flex flex-col">
      <div className="flex justify-between">
        <span>Total:</span>
        <span>৳{(Number(total) || 0).toFixed(2)}</span>
      </div>
      <div className="flex justify-between">
        <span>Discount ({discountType === "percentage" ? "%" : "৳"}):</span>
        <span>{(Number(discount) || 0).toFixed(2)}</span>
      </div>
      {Number(dueAmount) > 0 && (
        <div className="flex justify-between">
          <span>Due Amount:</span>
          <span>৳{(Number(dueAmount) || 0).toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between">
        <span>Net Amount:</span>
        <span>৳{(Number(netAmount) || 0).toFixed(2)}</span>
      </div>
    </div>
  );

  const slipFooter = (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", paddingLeft: "10px", paddingRight: "10px" }}>
      <div style={{ textAlign: "center", width: "220px" }}>
        <div style={{ borderTop: "1px solid #000", marginBottom: "8px" }}></div>
        <p style={{ margin: 0, fontWeight: "bold", fontSize: "11pt", textTransform: "capitalize" }}>
          {billDetails.userName || "N/A"}
        </p>
        <p style={{ margin: "5px 0 0", fontSize: "9pt" }}>Prepared By</p>
      </div>
      <div style={{ textAlign: "center", width: "220px" }}>
        <div style={{ borderTop: "1px solid #000", marginBottom: "8px" }}></div>
        <p style={{ margin: 0, fontWeight: "bold", fontSize: "11pt" }}>
          {authorizedBy || "N/A"}
        </p>
        <p style={{ margin: "5px 0 0", fontSize: "9pt" }}>Authorized By</p>
      </div>
    </div>
  );

  const slipIsPaid = (Number(dueAmount) || 0) <= 0;
  const slipAmountInWords = numberToWordsBD(netAmount) + " Taka Only";

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto">
        <div className="bg-white w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto print-content">
          {/* Default Modal View */}
          <div className="print-hidden">
            <div className="text-M-text-color p-4 rounded-t-lg flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-gray-200 border-b border-dashed border-gray-300">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Pathology Bill Details
                </h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handlePrintTestReport}
                  className="text-M-primary-color hover:text-white size-8 sm:size-7 p-1 rounded-full bg-white flex items-center justify-center hover:bg-M-primary-color transition-all duration-300"
                  title="Print Test Report"
                >
                  <FaBars size={14} />
                </button>
                <button
                  onClick={handlePrintBill}
                  className="text-M-primary-color hover:text-white size-8 sm:size-7 p-1 rounded-full bg-white flex items-center justify-center hover:bg-M-primary-color transition-all duration-300"
                  title="Print Bill"
                >
                  <BsPrinter size={14} />
                </button>
                {!isReadOnly && (
                  <>
                    <button
                      onClick={handleEdit}
                      className="text-M-Green-color hover:text-white size-8 sm:size-7 p-1 rounded-full bg-white flex items-center justify-center hover:bg-M-Green-color transition-all duration-300"
                      title="Edit"
                    >
                      <FiEdit size={14} />
                    </button>
                    <button
                      onClick={handleDelete}
                      className="text-M-secondary-color hover:text-white size-8 sm:size-7 p-1 rounded-full bg-white flex items-center justify-center hover:bg-M-secondary-color transition-all duration-300"
                      title="Delete"
                    >
                      <RiDeleteBin6Line size={14} />
                    </button>
                  </>
                )}
                <button
                  onClick={onClose}
                  className="text-M-secondary-color hover:text-white size-8 sm:size-7 p-1 rounded-full bg-white flex items-center justify-center hover:bg-M-secondary-color transition-all duration-300"
                  title="Close"
                >
                  <HiXMark size={14} />
                </button>
              </div>
            </div>
            <div className="p-4">
              <div>
                <div className="mt-4 sm:flex justify-between items-start mb-2">
                  <div>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Invoice No</span>
                      :<span className="text-gray-600 pl-1">{billNumber}</span>
                    </p>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Billing Date</span>
                      :
                      <span className="text-gray-600 pl-1">
                        {new Date(createdAt).toLocaleDateString("en-US")}
                      </span>
                    </p>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Patient Name</span>
                      :<span className="text-gray-600 pl-1">{patientName || "N/A"}</span>
                    </p>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Payment Mode</span>
                      :<span className="text-gray-600 capitalize pl-1">{paymentMode || "N/A"}</span>
                    </p>
                  </div>
                  <div>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Doctor Name</span>
                      :<span className="text-gray-600 pl-1">{doctorName || "N/A"}</span>
                    </p>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Referring Doctor</span>
                      :<span className="text-gray-600 pl-1">{referDoctorName || "N/A"}</span>
                    </p>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Donor</span>
                      :<span className="text-gray-600 pl-1">{donorName || "N/A"}</span>
                    </p>
                    <p className="text-black mb-1 text-[15px]">
                      <span className="w-[120px] inline-block font-medium">Authorized By</span>
                      :<span className="text-gray-600 pl-1">{authorizedBy || "N/A"}</span>
                    </p>
                  </div>
                </div>
              </div>
              {tests && tests.length > 0 && (
                <div className="mt-4 sm:mt-6">
                  <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">
                    Test Details
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-[781px] w-full border-collapse text-xs sm:text-sm">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-2 sm:p-3 border font-semibold">Test Name</th>
                          <th className="p-2 sm:p-3 border font-semibold">Vat</th>
                          <th className="p-2 sm:p-3 border font-semibold text-end">Amount(৳)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tests.map((test, index) => (
                          <tr key={index}>
                            <td className="p-2 sm:p-3 border text-center">{test.testName || "N/A"}</td>
                            <td className="p-2 sm:p-3 border text-center">{test.taxPercent ? `${test.taxPercent}%` : "0%"}</td>
                            <td className="p-2 sm:p-3 border text-end">{test.amount ? test.amount.toFixed(2) : "0.00"}</td>
                          </tr>
                        ))}
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3 pt-4" colSpan="2">
                            <p><strong>Sub Total (৳) :</strong></p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                            <p>{total ? total.toFixed(2) : "0.00"}</p>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="2">
                            <p><strong>Vat (amount) (৳) :</strong></p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                            <p>{taxAmount ? taxAmount.toFixed(2) : "0.00"}</p>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="2">
                            <p><strong>Discount (%) :</strong></p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                            <p>{discount ? discount.toFixed(2) : "0.00"}</p>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800" colSpan="2">
                            <strong className="py-1 px-3 text-sm">Net Total (৳) :</strong>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800">
                            <strong className="py-1 px-3 border-t border-gray-400/20 block">
                              {netAmount ? netAmount.toFixed(2) : "0.00"}
                            </strong>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800" colSpan="2">
                            <strong className="py-1 px-3 text-sm">Total Paid Amount (৳) :</strong>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800">
                            <strong className="py-1 px-3">{paymentAmount ? paymentAmount.toFixed(2) : "0.00"}</strong>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="2">
                            <p><strong>Return Amount (৳) :</strong></p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                            <p>{returnAmount ? returnAmount.toFixed(2) : "0.00"}</p>
                          </td>
                        </tr>
                        <tr>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3" colSpan="2">
                            <p><strong>Due Amount (৳) :</strong></p>
                          </td>
                          <td className="text-end capitalize font-inter font-normal text-sm text-gray-800 py-1 px-3">
                            <p>{dueAmount ? dueAmount.toFixed(2) : "0.00"}</p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {payments && payments.length > 0 && (
                <div className="mt-8 border-t border-dashed border-gray-300 pt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <span className="w-2 h-6 bg-amber-500 rounded-full"></span>
                    Payment & Correction History
                  </h3>
                  <div className="space-y-3">
                    {payments.map((payment, index) => (
                      <div
                        key={payment.id}
                        className={`p-4 rounded-lg border-l-4 shadow-sm ${
                          payment.amount < 0
                            ? "bg-red-50 border-red-400"
                            : index === payments.length - 1 && payments.length > 1
                            ? "bg-blue-50 border-blue-400"
                            : "bg-green-50 border-green-400"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[10px] uppercase tracking-wider font-bold text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200">
                                {new Date(payment.paymentDate).toLocaleString("en-US", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                              {index === 0 ? (
                                <span className="text-[10px] text-green-600 font-bold uppercase">Initial Entry</span>
                              ) : (
                                <span className="text-[10px] text-amber-600 font-bold uppercase">Adjustment</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-700 font-medium leading-relaxed">
                              {payment.note || "No details provided for this transaction."}
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <p className={`text-base font-bold ${payment.amount < 0 ? "text-red-600" : "text-green-700"}`}>
                              {payment.amount > 0 ? "+" : ""}{payment.amount.toFixed(2)} ৳
                            </p>
                            <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-tight">
                              Via {payment.paymentMode || "Cash"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="mt-4 text-[11px] text-gray-400 italic">
                    * Negative amounts indicate a reduction in the previously recorded paid amount.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* NEW A5 B&W slip for Full Bill (only rendered while printing the bill) */}
          {printType === "bill" && (
            <PathologyPrintSlip
              className="print-layout"
              title="Pathology Invoice"
              billInfo={slipBillInfo}
              table={slipTable}
              summary={slipSummary}
              footer={slipFooter}
              isPaid={slipIsPaid}
              amountInWords={slipAmountInWords}
            />
          )}

          {/* Test Report now uses the same A5 slip design (no financial summary / no stamp) */}
          {printType === "test" && (
            <PathologyPrintSlip
              className="print-test-report-layout"
              title="Test Report"
              billInfo={slipBillInfo}
              table={slipTable}
              footer={slipFooter}
            />
          )}
        </div>
      </div>
      {isEditModalOpen && (
        <AddBill
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            onClose();
          }}
          billDetails={{
            ...billDetails,
            tests: tests,
          }}
          isEditMode={true}
          onSave={async (message, updatedBill, closeAddBill) => {
            await onSave(message, updatedBill);
            if (closeAddBill) {
              setIsEditModalOpen(false);
            }
          }}
        />
      )}
    </>
  );
};

export default BillDetailsModal;